import { Injectable } from '@angular/core';
import { Model } from 'survey-core';
import { firstValueFrom } from 'rxjs';
import { PersonRelationship, RequestParameters } from '@tangilla/tg-ng-models';
import { SurveyService } from '../survey/survey.service';
import { MappedFieldsService } from './mapped-fields.service';
import {
  MemberApiService,
  NrdsAssociationsApiService,
  OfficesApiService,
  PeopleApiService
} from '@tangilla/tg-ng-core';
import { ErrorsService } from './errors.service';

@Injectable({
  providedIn: 'root'
})
export class RecordMappingService {
  isLoading = false;

  constructor(private surveyService: SurveyService,
              private errorsService: ErrorsService,
              private mappedFieldsService: MappedFieldsService,
              private memberApiService: MemberApiService,
              private officesApiService: OfficesApiService,
              private nrdsAssociationsApiService: NrdsAssociationsApiService,
              private peopleApiService: PeopleApiService) {
  }

  async processRecordMapping(model: Model, personId: any) {
    this.isLoading = true;
    try {
      await this.getPerson(model, personId);
      await this.getPersonRelationships(model, personId);
      await this.getMember(model);
    } catch (error) {
      this.errorsService.processError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async getPerson(model: Model, personId: any) {
    this.surveyService.personRecord = await firstValueFrom(this.peopleApiService.getPerson(personId));
    this.mappedFieldsService.processMappedData(model,
      'person',
      this.surveyService.personRecord);
  }

  async getPersonRelationships(model: Model, personId: any) {
    const personRelationships = await firstValueFrom(this.peopleApiService.getPersonRelationships(new RequestParameters(null), true, personId));
    await this.processPersonRelationships(model, personRelationships.items);
  }

  async processPersonRelationships(model: Model, personRelationships: PersonRelationship[]) {
    this.surveyService.personRelationships = personRelationships;
    this.mappedFieldsService.processMappedData(model,
      'person_relationships',
      this.surveyService.personRelationships);
  }

  async getMember(model: Model) {
    if (this.surveyService.personRecord?.member_nrds_id) {
      model.setVariable('person_relationships', this.surveyService.personRelationships);
      const personRelationshipsWithNRDS = this.surveyService.personRelationships
        .filter(function (relationship) {
          return relationship.role_type === 'R';
        });
      if (personRelationshipsWithNRDS.length > 0) {
        await this.getMemberRecord(model, this.surveyService.personRecord?.member_nrds_id);
      }
    }
  }

  async getMemberRecord(model: Model, memberNrdsId: string) {
    this.surveyService.memberRecord = await firstValueFrom(this.memberApiService.getMember(memberNrdsId));
    await this.processMemberRecord(model);
  }

  async processMemberRecord(model: Model) {
    this.mappedFieldsService.processMappedData(model,
      'member',
      this.surveyService.memberRecord);

    let officeNrdsId = this.surveyService.memberRecord?.office_nrds_id;
    if (this.surveyService.memberRecord.local_association_id !== model.getVariable('v-association_nrds')) {
      // Get the first relationship office if the member is not primary
      officeNrdsId = this.surveyService.personRelationships[0]?.office_nrds_id;
    }

    await this.getOfficeRecord(model, this.surveyService.officeId || officeNrdsId);
    await this.getAssociationRecord(model, this.surveyService.memberRecord.local_association_id);
  }

  async getOfficeRecord(model: Model, officeNrdsId: string) {
    this.surveyService.officeRecord = await firstValueFrom(this.officesApiService.getOffice(officeNrdsId));
    this.mappedFieldsService.processMappedData(model,
      'office',
      this.surveyService.officeRecord);
  }

  async getAssociationRecord(model: Model, localAssociationNrdsId: string) {
    const associations = await firstValueFrom(
      this.nrdsAssociationsApiService.getAll((new RequestParameters(null))
        .setAdditionalFields({association_nrds_id: localAssociationNrdsId}))
    );
    this.surveyService.associationRecord = associations.length > 0 ? associations[0] : null;
    this.mappedFieldsService.processMappedData(model,
      'association',
      this.surveyService.associationRecord);
  }

}
